/* ------------------------------------------------------------------
  File: navConfig.js
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-08-20
------------------------------------------------------------------ */

// Purpose:
//   - navConfig defines the primary website navigation structure.
//   - It supports direct navigation links and full mega menu groups.
//   - All route values should come from routesConfig to maintain a single
//     source of truth for application paths.

import { routesConfig, routeBuilders } from "./routesConfig";

// Media Imports
import aboutFeaturedImage from "../../assets/images/glass-pyramid-entrance-of-the-louvre.jpg" ;
import businessesFeaturedImage from "../../assets/images/pyramid-of-the-sun.jpg" ;
import investorsFeaturedImage from "../../assets/images/city-landscape-at-night.jpg" ;
import newsroomFeaturedImage from "../../assets/images/a-beautiful-mountain-landscape-in-spain.jpg" ;
import careerseaturedImage from "../../assets/images/landscape-of-brown-grass-with-rocky-mountains.jpg" ;

export const navConfig = [
  {
    label: "Home",
    to: routesConfig.home,
  },

  // ====== ABOUT US ========================
  {
    label: "About Us",
    to: routesConfig.about,
    children: [
      {
        label: "Our Philosophy",
        to: routesConfig.aboutPhilosophy,
        description:
          "The principles that guide how Accend™ builds, invests, and creates lasting value across Africa.",
      },
      {
        label: "Our Leadership",
        to: routesConfig.aboutLeadership,
        description:
          "The people directing capital, engineering, and operational discipline across the Group.",
      },
      {
        label: "Governance",
        to: routesConfig.aboutGovernance,
        description:
          "The structures and standards that underpin every Accend™ company.",
      },
      {
        label: "Holding Model",
        to: routesConfig.aboutHoldingModel,
        description:
          "How Accend™ develops, supports, and governs business across the Group.",
      },
      {
        label: "Sector Focus",
        to: routesConfig.aboutSectorFocus,
        description:
          "The industries where Accend deploys capital, engineering, technology, and research to close Africa's structural gaps.",
      },
      {
        label: "Partnerships",
        to: routesConfig.aboutPartnerships,
        description:
          "How Accend™ collaborates with investors, institutions, and strategic partners.",
      },
      {
        label: "Sustainability & Impact",
        to: routesConfig.aboutSustainabilityAndImpact,
        description:
          "Accend™'s commitment to responsible growth, sustainability, and positive impact.",
      },
    ],
    megaMenu: {
      eyebrow: "About Us",
      title: "Engineering Africa's Transformation.",
      description:
        "A Pan-African engineering and investment group building and scaling enterprises across Africa and beyond.",
      cta: {
        label: "About us",
        to: routesConfig.about,
      },
      columns: [
        {
          title: "Company Overview",
          items: [
            {
              label: "Who We Are",
              to: routesConfig.aboutWhoWeAre,
            },
            {
              label: "Our Purpose",
              to: routesConfig.aboutOurPurpose,
            },
            {
              label: "Mission & Vision",
              to: routesConfig.aboutMissionVision,
            },
            {
              label: "Philosophy",
              to: routesConfig.aboutPhilosophy,
            },
            {
              label: "Leadership",
              to: routesConfig.aboutLeadership,
            },
            {
              label: "Governance",
              to: routesConfig.aboutGovernance,
            },
          ],
        },
        {
          title: "Group Structure",
          items: [
            {
              label: "Holding Model",
              to: routesConfig.aboutHoldingModel,
            },
            {
              label: "Sector Focus",
              to: routesConfig.aboutSectorFocus,
            },
            {
              label: "Partnerships",
              to: routesConfig.aboutPartnerships,
            },
            {
              label: "Sustainability & Impact",
              to: routesConfig.aboutSustainabilityAndImpact,
            },
          ],
          cta: {
            label: "Explore the Group",
            to: routesConfig.aboutAccendGroup,
          },
        },
      ],
      feature: {
        image: aboutFeaturedImage,
        title: "Capital enables building. Scale transforms.",
        description:
          "The five layers of the Accend Growth Pyramid — from strategic capital to continental scale.",
        cta: {
          label: "See our approach",
          to: routesConfig.aboutGroupApproach,
        },
      },
    },
  },

  // ====== BUSINESS ========================
  {
    label: "Businesses",
    to: routesConfig.businesses,
    children: [
      {
        label: "Portfolio Overview",
        to: routesConfig.businessPortfolioOverview,
        description:
          "An overview of the businesses through which Accend™ develops and compounds long-term value.",
      },
      {
        label: "Operating Sectors",
        to: routesConfig.businessesOperatingSectors,
        description:
          "The sectors where Accend™ is positioned to build capabilities and drive growth.",
      },
    ],
    megaMenu: {
      eyebrow: "Businesses",
      title: "Building Locally. Scaling Globally.",
      description:
        "A diversified portfolio of specialized companies, each building strategic capabilities within its sector.",
      cta: {
        label: "Businesses",
        to: routesConfig.businesses,
      },
      columns: [
        {
          title: "Operating Sectors",
          items: [
            {
              label: "Technology & Digital Services",
              to: routeBuilders.businessesSector("technology-digital-services"),
            },
            {
              label: "Engineering & Infrastructure",
              to: routeBuilders.businessesSector("engineering-infrastructure"),
            },
            {
              label: "Networks & Telecommunications",
              to: routeBuilders.businessesSector("networks-telecommunications"),
            },
            {
              label: "Energy & Utilities",
              to: routeBuilders.businessesSector("energy-utilities"),
            },
            {
              label: "Financial Services & Capital",
              to: routeBuilders.businessesSector("financial-services-capital"),
            },
            {
              label: "Health & Life Sciences",
              to: routeBuilders.businessesSector("health-life-sciences"),
            },
            {
              label: "More Sectors",
              children: [
                {
                  label: "Logistics & Mobility",
                  to: routeBuilders.businessesSector("logistics-mobility"),
                },
                {
                  label: "Research & Innovation",
                  to: routeBuilders.businessesSector("research-innovation"),
                },
                {
                  label: "Agriculture & Food Systems",
                  to: routeBuilders.businessesSector(
                    "agriculture-food-systems"
                  ),
                },
                {
                  label: "Investment & Venture Building",
                  to: routeBuilders.businessesSector(
                    "investment-venture-building"
                  ),
                },
                {
                  label: "Emerging Ventures",
                  to: routeBuilders.businessesSector("emerging-ventures"),
                },
              ],
            },
          ],
        },
        {
          title: "Group Companies",
          items: [
            {
              label: "Accend Technologies™",
              to: routeBuilders.businessesGroupCompanies("accend-technologies"),
            },
            {
              label: "Accend Engineering™",
              to: routeBuilders.businessesGroupCompanies("accend-engineering"),
            },
            {
              label: "Accend Networks™",
              to: routeBuilders.businessesGroupCompanies("accend-networks"),
            },
            {
              label: "Accend Energies™",
              to: routeBuilders.businessesGroupCompanies("accend-energies"),
            },
            {
              label: "Accend Labs™",
              to: routeBuilders.businessesGroupCompanies("accend-labs"),
            },
          ],
          cta: {
            label: "Browse all companies",
            to: routesConfig.businessesGroupCompanies,
          },
        },
      ],
      feature: {
        image: businessesFeaturedImage,
        title: "One group. One mission. Limitless possibilities.",
        description:
          "Every company designed to solve a real problem, build lasting capability, and compete a global stage.",
        cta: {
          label: "View our portfolio",
          to: routesConfig.businessesPortfolioOverview,
        },
      },
    },
  },

  // ====== INVESTORS PATHS ========================
  {
    label: "Investors",
    to: routesConfig.investors,
    children: [
      {
        label: "Investor Overview",
        to: routesConfig.investorsOverview,
        description:
          "Accend Holdings' investment direction, portfolio approach, and long-term stakeholder value.",
      },
      {
        label: "Strategic Capital",
        to: routesConfig.investorsStrategicCapital,
        description:
          "How Accend™ deploys capital into businesses, technology, and infrastructure with long-term value.",
      },
      {
        label: "Risk & Governance",
        to: routesConfig.investorsRiskGovernance,
        description:
          "Our aproach to responsible growth, risk management, and institutional governance.",
      },
      {
        label: "Impact & Returns",
        to: routesConfig.investorsImpactReturns,
        description:
          "How Accend Holdings balances financial performance with lasting economic impact.",
      },
    ],
    megaMenu: {
      eyebrow: "Investors",
      title: "Patient Capital. Discipline Execution.",
      description:
        "Deploying strategic capital into businesses, technology, and infrastructure built for long-term value.",
      cta: {
        label: "Investors",
        to: routesConfig.investors
      },
      columns: [
        {
          title: "Investor Information",
          items: [
            {
              label: "Investor Overview",
              to: routesConfig.investorsOverview,
            },
            {
              label: "Strategic Capital",
              to: routesConfig.investorsStrategicCapital,
            },
            {
              label: "Risk & Governance",
              to: routesConfig.investorsRiskGovernance,
            },
            {
              label: "Impact & Returns",
              to: routesConfig.investorsImpactReturns,
            },
          ],
        },
        {
          title: "Investor Resources",
          items: [
            {
              label: "Reports & Publications",
              to: routesConfig.investorsReportsPublications,
            },
            {
              label: "Investment Outlook",
              to: routesConfig.investorsInvestmentOutlook,
            },
            {
              label: "Investor Contacts",
              to: routesConfig.contactForInvestors,
            },
            {
              label: "FAQ",
              to: routesConfig.investorsFaq,
            },
          ],
          cta: {
            label: "Browse all resources",
            to: routesConfig.investorsResources,
          },
        },
      ],
      feature: {
        image: investorsFeaturedImage,
        title: "Investing for long-term value creation.",
        description:
          "We prioritize enduring value over short-term — patient capital deployed for lasting impact.",
        cta: {
          label: "Our investment philosophy",
          to: routesConfig.investorsInvestmentPhilosophy,
        },
      },
    },
  },

  // ====== NEWSROOM ========================
  {
    label: "Newsroom",
    to: routesConfig.newsroom,
    children: [
      {
        label: "Latest Updates",
        to: routesConfig.newsroom,
        description:
          "News, announcements, and stories from across the Acend Group.",
      },
      {
        label: "Insights",
        to: routesConfig.newsroomInsights,
        description:
          "Perspectives on business, technology, investment, and Africa's development.",
      },
      {
        label: "Press Releases",
        to: routesConfig.newsroomPressReleases,
        description:
          "Official announcements, media statements, and corporate communications.",
      },
      {
        label: "Events",
        to: routesConfig.newsroomEvents,
        description:
          "Upcoming and past events, engagements, and public appearances.",
      },
    ],
    megaMenu: {
      eyebrow: "Newsroom",
      title: "Stories from Across the Group.",
      description:
        "News, insights, and announcements from the companies accelerating Africa's transformation.",
      cta: {
        label: "Newsroom",
        to: routesConfig.newsroom,
      },
      columns: [
        {
          title: "Categories",
          items: [
            {
              label: "Company News",
              to: routesConfig.newsroomCompanyNews,
            },
            {
              label: "Group Updates",
              to: routesConfig.newsroomGroupUpdates,
            },
            {
              label: "Insights",
              to: routesConfig.newsroomInsights,
            },
            {
              label: "Events & Conferences",
              to: routesConfig.newsroomEvents,
            },
            {
              label: "Press Releases",
              to: routesConfig.newsroomPressReleases,
            },
          ],
        },
        {
          title: "Topics",
          items: [
            {
              label: "Technology",
              to: routeBuilders.newsroomTopicsSearch("technology"),
            },
            {
              label: "Engineering",
              to: routeBuilders.newsroomTopicsSearch("engineering"),
            },
            {
              label: "Infrastructure",
              to: routeBuilders.newsroomTopicsSearch("infrastructure"),
            },
            {
              label: "Investment",
              to: routeBuilders.newsroomTopicsSearch("investment"),
            },
            {
              label: "Energy",
              to: routeBuilders.newsroomTopicsSearch("energy"),
            },
            {
              label: "Capital",
              to: routeBuilders.newsroomTopicsSearch("capital"),
            },
          ],
          cta: {
            label: "Browse all topics",
            to: routesConfig.newsroomTopicsSearch,
          },
        },
      ],
      feature: {
        image: newsroomFeaturedImage,
        title: "Follow the Group development progress.",
        description:
          "Get the latest updates from across the Accend Group delivered directly to your mail.",
        cta: {
          label: "Join mailing list",
          to: routesConfig.newsroomSignup,
        },
      },
    },
  },

  // ====== CAREERS ========================
  {
    label: "Careers",
    to: routesConfig.careers,
    children: [
      {
        label: "Life at Accend",
        to: routesConfig.careersAccendLife,
        description:
          "Our culture, values, and what it's like to build with the Accend Group.",
      },
      {
        label: "Open Positions",
        to: routesConfig.careersOpenPositions,
        description:
          "Available roles and opportunities across Accend Holdings and its subsidiaries.",
      },
      {
        label: "Apply at Accend™",
        to: routesConfig.careersApply,
        description:
          "Submit your application or express interest in future career opportunities.",
      },
    ],
    megaMenu: {
      eyebrow: "Careers",
      title: "Build Africa's future with the Accend Group.",
      description:
        "We're assembling world-class talent to engineer the companies and systems driving Africa's transformation.",
      cta: {
        label: "Careers",
        to: routesConfig.careers,
      },
      columns: [
        {
          title: "Opportunities",
          items: [
            {
              label: "All Opportunities",
              to: routesConfig.careersOpportunities,
            },
            {
              label: "Experienced Professionals",
              to: routesConfig.careersOpportunitiesForProfessionals,
            },
            {
              label: "Students & Graduates",
              to: routesConfig.careersOpportunitiesForInterns,
            },
            {
              label: "Global Talent",
              to: routesConfig.careersOpportunitiesGlobalTalent,
            },
          ],
        },
        {
          title: "Life at Accend",
          items: [
            {
              label: "Our Culture",
              to: routesConfig.careersCulture,
            },
            {
              label: "Career Development",
              to: routesConfig.careersCareerDevelopment,
            },
            {
              label: "Innovation & Research",
              to: routesConfig.careersInnovationAtAccend,
            },
            {
              label: "Diversity & Inclusion",
              to: routesConfig.careersDiversity,
            },
          ],
          cta: {
            label: "Join the team",
            to: routesConfig.careersApply,
          },
        },
      ],
      feature: {
        image: careerseaturedImage,
        title: "Build solutions that compete globally.",
        description:
          "Join engineers, researchers, and technologists building African companies and technologies for the world.",
        cta: {
          label: "View open positons",
          to: routesConfig.careersOpenPositions,
        },
      },
    },
  },

  // ====== CONTACT ========================
  {
    label: "Contact",
    to: routesConfig.contact,
  },
];